import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Global, css } from '@emotion/react';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatNumber,
  StatHelpText,
  FormControl,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Stack,
  SimpleGrid,
  HStack,
  Tag,
  Icon,
  Tooltip as ChakraTooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon, InfoOutlineIcon, ArrowForwardIcon } from '@chakra-ui/icons';
import { format, parseISO } from 'date-fns';
import { ResponsiveContainer, Tooltip, Bar, XAxis, YAxis, CartesianGrid, Line, ComposedChart, ReferenceLine } from 'recharts';
import AppLayout from '../components/layout/AppLayout';
import { useAuthStore } from '../store/authStore';
import { useOptimizedUserStore as useUserStore } from '../store/optimizedUserStore';
import {
  getTodayISOString,
  roundToDecimal,
  groupEntriesByMonthWithAllDays,
  isDateInFuture
} from '../utils/dateUtils';
import { DayEntry } from '../types';

// Component to render a single day entry with its own state
const DayEntryRow = ({ day, onSaveHours, onDeleteEntry, workWeekDays = [] }: { 
  day: DayEntry; 
  onSaveHours: (day: DayEntry, hours: number) => void;
  onDeleteEntry: (id: string, entryDate: string) => void;
  workWeekDays?: number[];
}) => {
  const hours = day.entry ? day.entry.billed_hours : 0;
  const [currentHours, setCurrentHours] = useState<string>(hours.toString());
  const isWeekend = day.dayOfWeek === 0 || day.dayOfWeek === 6;
  const isToday = day.date === getTodayISOString();
  const isWorkDay = workWeekDays.includes(day.dayOfWeek);
  
  const weekendBgColor = useColorModeValue('gray.50', 'gray.700');
  const nonWorkdayBgColor = useColorModeValue('gray.200', 'gray.650'); // Darker background for non-workdays
  const workdayBgColor = useColorModeValue('green.50', 'green.900');
  const todayBgColor = useColorModeValue('blue.50', 'blue.900');
  const inputBgColor = useColorModeValue('white', 'gray.600');
  const inputHoverBgColor = useColorModeValue('gray.50', 'gray.500');
  
  // Choose background color based on day type
  let rowBgColor = nonWorkdayBgColor; // Default to non-workday color
  if (isToday) {
    rowBgColor = todayBgColor;
  } else if (isWorkDay) {
    rowBgColor = workdayBgColor;
  } else if (isWeekend) {
    rowBgColor = weekendBgColor;
  }
  
  // Update local state when the entry changes and handle edge cases
  useEffect(() => {
    // Only update if the value has actually changed to avoid unnecessary re-renders
    // Convert both to strings for consistent comparison
    const newHoursString = hours.toString();
    if (currentHours !== newHoursString) {
      setCurrentHours(newHoursString);
    }
  }, [hours]);
  
  // Handle Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Only save if the hours have changed
      if (parseFloat(currentHours) !== hours) {
        onSaveHours(day, parseFloat(currentHours));
      }
    }
  };

  return (
    <Box 
      bg={rowBgColor}
      borderWidth={isToday ? "1px" : "0"}
      borderColor={isToday ? "blue.200" : "transparent"}
      borderRadius="md"
      p={3}
      transition="all 0.2s"
      _hover={{ boxShadow: "sm" }}
    >
      <Flex justify="space-between" align="center">
        <HStack spacing={2} flex="1">
          <Text fontSize="sm" color={isWeekend ? 'gray.500' : undefined}>
            {day.displayDate}
          </Text>
          {day.hasEntry && !isToday && (
            <Tag size="sm" colorScheme="blue" variant="subtle" borderRadius="full">
              {hours} hrs
            </Tag>
          )}
          {isToday && (
            <Tag size="sm" colorScheme="purple" variant="subtle" borderRadius="full">
              Today
            </Tag>
          )}
        </HStack>
        
        <Flex align="center">
          <NumberInput
            value={currentHours}
            onChange={(value) => {
              const numValue = parseFloat(value);
              // Enforce the max of 24 hours
              if (numValue > 24) {
                setCurrentHours("24");
              } else {
                setCurrentHours(value);
              }
            }}
            size="sm"
            w="85px"
            min={0}
            max={24}
            step={0.25}
            mr={2}
            variant="filled"
            bg={inputBgColor}
            borderColor="gray.300"
            borderWidth="1px"
            _hover={{ bg: inputHoverBgColor }}
            keepWithinRange={true}
            clampValueOnBlur={true}
          >
            <NumberInputField 
              fontSize="sm" 
              bg={inputBgColor}
              _hover={{ bg: inputHoverBgColor }}
              _focus={{ bg: 'white', borderColor: 'blue.500' }}
              textAlign="center"
              onKeyDown={handleKeyDown}
            />
          </NumberInput>
          
          <Button
            size="xs"
            colorScheme={parseFloat(currentHours) !== hours ? "blue" : "gray"}
            onClick={() => onSaveHours(day, parseFloat(currentHours))}
            isDisabled={parseFloat(currentHours) === hours}
          >
            {day.hasEntry ? 'Update' : 'Add'}
          </Button>
          
          {day.hasEntry && (
            <Button
              size="xs"
              colorScheme="red"
              variant="ghost"
              ml={1}
              onClick={() => onDeleteEntry(day.entry!.id, day.date)}
            >
              Clear
            </Button>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

// Status indicator component for quick visual reference
const StatusIndicator = ({ status, tooltipText }: { 
  status: 'ahead' | 'behind' | 'on-track';
  tooltipText?: string;
}) => {
  const colorScheme = 
    status === 'ahead' ? 'green' :
    status === 'behind' ? 'red' : 'blue';
  
  const label = 
    status === 'ahead' ? 'Ahead' :
    status === 'behind' ? 'Behind' : 'On Track';
  
  const statusTag = (
    <Tag 
      size="sm" 
      colorScheme={colorScheme} 
      borderRadius="full" 
      fontWeight="medium"
      display="flex"
      justifyContent="center"
      alignItems="center"
      px={2}
      minW="65px"
      textAlign="center"
    >
      {label}
    </Tag>
  );
  
  if (tooltipText) {
    return (
      <ChakraTooltip label={tooltipText} placement="top" hasArrow>
        {statusTag}
      </ChakraTooltip>
    );
  }
    
  return statusTag;
};

// Mini stat card that shows a single important metric with an optional indicator
const MiniStatCard = ({ label, value, helpText, status, statusTooltip, icon }: {
  label: string;
  value: string | number;
  helpText?: string;
  status?: 'ahead' | 'behind' | 'on-track';
  statusTooltip?: string;
  icon?: React.ReactNode;
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  
  return (
    <Card bg={cardBg} boxShadow="md" height="100%" 
    transition="transform 0.2s"
    _hover={{ transform: 'translateY(-2px)' }}
    >
      <CardBody p={4}>
        <HStack justifyContent="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="medium" color="gray.500">{label}</Text>
          {status && <StatusIndicator status={status} tooltipText={statusTooltip} />}
          {icon}
        </HStack>
        <Stat>
          <StatNumber fontSize="xl" lineHeight="1.2">{value}</StatNumber>
          {helpText && <StatHelpText mt={1} mb={0}>{helpText}</StatHelpText>}
        </Stat>
      </CardBody>
    </Card>
  );
};

// CSS for view transitions
const ViewTransitionStyles = () => (
  <Global
    styles={css`
      .chart-container {
        transition: height 0.3s ease;
      }
    `}
  />
);

// Define BarChartIcon component
const BarChartIcon = (props: any) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M3 3v17a1 1 0 0 0 1 1h17v-2H5V3H3z"
    />
    <path
      fill="currentColor"
      d="M19 5h-3c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h3c.55 0 1-.45 1-1V6c0-.55-.45-1-1-1zm-1 10h-1V7h1v8zM14 9h-3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h3c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zm-1 6h-1v-4h1v4zM9 13H6c-.55 0-1 .45-1 1v2c0 .55.45 1 1 1h3c.55 0 1-.45 1-1v-2c0-.55-.45-1-1-1zm-1 2H7v-1h1v1z"
    />
  </Icon>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthStore();
  const {
    profile,
    dailyEntries,
    dailyTargets,
    calculations,
    targetSummary,
    error,
    fetchVacationDays,
    fetchDailyEntries,
    fetchDailyTargets,
    fetchTargetSummary,
    fetchTargetExceptions,
    refreshDailyTargets,
    logDailyHours,
    editDailyHours,
    deleteDailyHours,
    profileLoading,
    vacationLoading,
    entriesLoading,
    targetsLoading,
    summaryLoading,
    exceptionsLoading,
  } = useUserStore();

  const loggedColor = useColorModeValue('#4299E1', '#63B3ED');    // Blue - logged hours
  const surplusColor = useColorModeValue('#48BB78', '#68D391');   // Green - surplus hours
  const deficitColor = useColorModeValue('#F56565', '#FC8181');   // Red - deficit hours
  const targetColor = useColorModeValue('#805AD5', '#9F7AEA');    // Purple - target line
  const trendColor = useColorModeValue('#ED8936', '#F6AD55');     // Orange - trend line
  const completedColor = useColorModeValue('#4299E1', '#63B3ED'); // Blue - completed hours
  const remainingColor = useColorModeValue('#EDF2F7', '#2D3748');  // Gray - remaining hours

  const todayInputBgColor = useColorModeValue("white", "gray.600");
  const todayInputHoverBgColor = useColorModeValue('gray.50', 'gray.500');

  const [todayHours, setTodayHours] = useState<string>('0');
  const [todayEntry, setTodayEntry] = useState<any>(null);
  const [savingToday, setSavingToday] = useState(false);
  const [showCumulativeChart, setShowCumulativeChart] = useState(false);
  const initialLoadPerformedRef = useRef(false);

  const isDashboardLoading = profileLoading || vacationLoading || entriesLoading || targetsLoading || summaryLoading || exceptionsLoading;

  const currentProfileId = profile?.id;
  useEffect(() => {
      initialLoadPerformedRef.current = false;
  }, [currentProfileId]);

  useEffect(() => {
    const loadDashboardData = async () => {
      initialLoadPerformedRef.current = true;
      try {
        await Promise.all([
          fetchVacationDays(),
          fetchDailyEntries(),
          fetchTargetSummary(),
          fetchTargetExceptions(),
          (async () => {
            const currentTargets = useUserStore.getState().dailyTargets;
            if (!currentTargets || currentTargets.length === 0) {
              try {
                await fetchDailyTargets();
                if (useUserStore.getState().dailyTargets.length === 0) {
                  await refreshDailyTargets();
                }
              } catch (err) {
                console.warn('Error fetching/refreshing daily targets:', err);
              }
            } else {
              await fetchDailyTargets();
            }
          })()
        ]);
      } catch (err) {
        console.error('Error during concurrent dashboard data fetch:', err);
      }
    };

    if (!authLoading && user && profile && !initialLoadPerformedRef.current) {
      loadDashboardData();
    }
  }, [user, authLoading, profile, fetchVacationDays, fetchDailyEntries, fetchTargetSummary, fetchTargetExceptions, fetchDailyTargets, refreshDailyTargets]);

  useEffect(() => {
    if (!authLoading && user && profile && !profile.setup_completed) {
      navigate('/setup');
    }
  }, [authLoading, user, profile, navigate]);

  useEffect(() => {
    if (!dailyEntries || dailyEntries.length === 0) {
       setTodayEntry(null);
       setTodayHours('0');
       return;
    }

    const todayISOString = getTodayISOString();
    const entry = dailyEntries.find((entry: any) => entry.entry_date === todayISOString);

    if (entry) {
      setTodayEntry(entry);
      if (parseFloat(todayHours) !== entry.billed_hours) {
        setTodayHours(entry.billed_hours.toString());
      }
    } else {
      setTodayEntry(null);
      setTodayHours('0');
    }
  }, [dailyEntries]);

  const monthlyBreakdown = useMemo(() => 
    profile
      ? groupEntriesByMonthWithAllDays(
          dailyEntries, 
          profile.tracking_start_date, 
          profile.tracking_end_date
        )
      : [],
    [profile, dailyEntries]
  );

  const sortedMonthlyBreakdown = useMemo(() => {
    return [...monthlyBreakdown].sort((a, b) => {
      return new Date(b.month).getTime() - new Date(a.month).getTime();
    });
  }, [monthlyBreakdown]);
  
  const dailyHoursData = useMemo(() => {
    if (!monthlyBreakdown.length || (!calculations && !targetSummary)) return [];
    
    const allDays = monthlyBreakdown.flatMap(month => month.days || []);
    const recentDays = allDays.slice(-14);
    
    const targetsByDate = new Map();
    dailyTargets.forEach((target: any) => {
      targetsByDate.set(target.target_date, target);
    });
    
    const avgRequired = targetSummary ? targetSummary.avg_daily_target : 
                       (calculations ? calculations.requiredAverageDailyHours : 0);
    
    let runningTotal = 0;
    let cumulativeTarget = 0;
    
    return recentDays.map((day, index) => {
      const dailyTarget = targetsByDate.get(day.date);
      
      const logged = day.entry ? day.entry.billed_hours : 0;
      
      const dailyTargetValue = dailyTarget ? dailyTarget.required_daily_target : avgRequired;
      const deficit = logged < dailyTargetValue ? dailyTargetValue - logged : 0;
      const surplus = logged > dailyTargetValue ? logged - dailyTargetValue : 0;
      
      runningTotal += logged;
      cumulativeTarget += dailyTargetValue;
      
      const trendValue = runningTotal - cumulativeTarget;
      const onTrack = Math.abs(trendValue) < (avgRequired * 0.5);
      
      return {
        date: day.date,
        displayDate: day.displayDate,
        shortDate: day.displayDate.split(', ')[1],
        dayIndex: index + 1,
        logged,
        avgRequired,
        dailyTarget: dailyTargetValue,
        deficit,
        surplus,
        cumulativeLogged: runningTotal,
        cumulativeTarget: cumulativeTarget,
        trend: trendValue,
        onTrack,
        isWorkDay: dailyTarget ? dailyTarget.is_work_day : profile?.work_week_days.includes(day.dayOfWeek)
      };
    });
  }, [monthlyBreakdown, calculations, targetSummary, profile, dailyTargets]);

  const cumulativeHoursData = useMemo(() => {
    if (!monthlyBreakdown.length || !profile) return [];
    
    const allDays = monthlyBreakdown.flatMap(month => month.days || []);
    if (allDays.length === 0) return [];
    
    const targetsByDate = new Map();
    dailyTargets.forEach((target: any) => {
      targetsByDate.set(target.target_date, target);
    });
    
    const avgRequired = targetSummary 
      ? targetSummary.avg_daily_target 
      : (calculations ? calculations.requiredAverageDailyHours : 0);
    
    const yearlyTarget = targetSummary 
      ? targetSummary.yearly_target_hours 
      : (profile ? profile.yearly_target_hours : 0);
    
    const startDate = new Date(profile.tracking_start_date);
    const endDate = new Date(profile.tracking_end_date);
    const totalTrackingPeriodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    let cumulativeLogged = 0;
    let cumulativeTarget = 0;
    
    return allDays.map((day, index) => {
      const dailyTarget = targetsByDate.get(day.date);
      const logged = day.entry ? day.entry.billed_hours : 0;
      const dailyTargetValue = dailyTarget ? dailyTarget.required_daily_target : avgRequired;
      
      cumulativeLogged += logged;
      cumulativeTarget += dailyTargetValue;
      
      const percentOfYearlyTarget = yearlyTarget > 0 
        ? roundToDecimal((cumulativeLogged / yearlyTarget) * 100) 
        : 0;
      
      const currentDate = new Date(day.date);
      const daysElapsed = Math.ceil((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const progressRatio = daysElapsed / totalTrackingPeriodDays;
      const projectedIdealProgress = roundToDecimal(yearlyTarget * progressRatio);
      
      return {
        date: day.date,
        displayDate: day.displayDate,
        shortDate: day.displayDate.split(', ')[1],
        dayNumber: index + 1,
        totalDays: allDays.length,
        daysFromStart: daysElapsed,
        totalPeriodDays: totalTrackingPeriodDays,
        logged,
        dailyTarget: dailyTargetValue,
        cumulativeLogged: roundToDecimal(cumulativeLogged),
        cumulativeTarget: roundToDecimal(cumulativeTarget),
        percentOfYearlyTarget,
        projectedIdealProgress,
        isWorkDay: dailyTarget ? dailyTarget.is_work_day : profile?.work_week_days.includes(day.dayOfWeek)
      };
    });
  }, [monthlyBreakdown, calculations, targetSummary, dailyTargets, profile]);

  const trendChartData = useMemo(() => {
    if (!monthlyBreakdown.length || !profile || (!targetSummary && !calculations)) return [];

    const allDays = monthlyBreakdown.flatMap(month => month.days || []);
    if (allDays.length === 0) return [];

    const recentDays = allDays.slice(-7);
    if (recentDays.length === 0) return [];

    const targetsByDate = new Map();
    dailyTargets.forEach((target: any) => {
      targetsByDate.set(target.target_date, target);
    });

    const avgRequired = targetSummary ? targetSummary.avg_daily_target :
                       (calculations ? calculations.requiredAverageDailyHours : 0);

    let initialLoggedHours = 0;
    let initialTargetHours = 0;
    const windowStartDate = recentDays[0].date;

    allDays.forEach(day => {
      if (day.date < windowStartDate) {
        const logged = day.entry ? day.entry.billed_hours : 0;
        const dailyTarget = targetsByDate.get(day.date);
        const dailyTargetValue = dailyTarget ? dailyTarget.required_daily_target : avgRequired;

        initialLoggedHours += logged;
        initialTargetHours += dailyTargetValue;
      }
    });

    let currentOverallLogged = initialLoggedHours;
    let currentOverallTarget = initialTargetHours;

    return recentDays.map((day, index) => {
      const dailyTarget = targetsByDate.get(day.date);
      const logged = day.entry ? day.entry.billed_hours : 0;
      const dailyTargetValue = dailyTarget ? dailyTarget.required_daily_target : avgRequired;

      currentOverallLogged += logged;
      currentOverallTarget += dailyTargetValue;

      let percentOfTarget;
      if (currentOverallTarget > 0) {
        percentOfTarget = roundToDecimal((currentOverallLogged / currentOverallTarget) * 100);
      } else {
        percentOfTarget = currentOverallLogged > 0 ? 120 : 0;
      }

      let status: 'ahead' | 'behind' | 'on-track' = 'on-track';
      if (percentOfTarget < 98) status = 'behind';
      if (percentOfTarget > 102) status = 'ahead';

      return {
        date: day.date,
        displayDate: day.displayDate,
        shortDate: day.displayDate.split(', ')[1],
        dayLabel: `Day ${index + 1}`,
        logged,
        dailyTarget: dailyTargetValue,
        cumulativeLogged: roundToDecimal(currentOverallLogged),
        cumulativeTarget: roundToDecimal(currentOverallTarget),
        percentOfTarget,
        status
      };
    });
  }, [monthlyBreakdown, calculations, targetSummary, dailyTargets, profile]);

  const trendChartYDomain = useMemo(() => {
    if (!trendChartData || trendChartData.length === 0) {
      return [90, 110];
    }

    const values = trendChartData.map(d => d.percentOfTarget);
    let minPercent = Math.min(...values);
    let maxPercent = Math.max(...values);

    if (minPercent === maxPercent) {
      const lower = Math.max(0, minPercent - 5);
      const upper = Math.min(120, maxPercent + 5);
      return [lower, upper];
    }

    const range = maxPercent - minPercent;
    const padding = Math.max(2, range * 0.1);

    let lowerBound = Math.floor(minPercent - padding);
    let upperBound = Math.ceil(maxPercent + padding);

    if (upperBound - lowerBound < 10) {
      const mid = (upperBound + lowerBound) / 2;
      lowerBound = Math.floor(mid - 5);
      upperBound = Math.ceil(mid + 5);
    }
    
    lowerBound = Math.max(0, lowerBound);
    upperBound = Math.min(120, upperBound);

    if (lowerBound >= upperBound) {
        lowerBound = upperBound - 10;
        lowerBound = Math.max(0, lowerBound);
    }

    return [lowerBound, upperBound];
  }, [trendChartData]);

  const chartConfig = useMemo(() => {
    return {
      colors: {
        logged: loggedColor,
        surplus: surplusColor,
        deficit: deficitColor,
        target: targetColor,
        trend: trendColor,
        completed: completedColor,
        remaining: remainingColor
      }
    };
  }, [loggedColor, surplusColor, deficitColor, targetColor, trendColor, completedColor, remainingColor]);

  const progressStatus = useMemo(() => {
    if (!targetSummary && !calculations) return 'on-track';
    
    const progressPercent = targetSummary && targetSummary.yearly_target_hours > 0
      ? (targetSummary.total_billed_hours / targetSummary.yearly_target_hours) * 100
      : calculations?.progressPercentage || 0;
    
    const targetPercent = targetSummary && targetSummary.total_work_days > 0
      ? (targetSummary.elapsed_work_days / targetSummary.total_work_days) * 100
      : (calculations && calculations.totalWorkDays > 0 
          ? ((calculations.totalWorkDays - calculations.remainingWorkDays) / calculations.totalWorkDays) * 100 
          : 0);
    
    if (progressPercent > targetPercent + 5) return 'ahead';
    if (progressPercent < targetPercent - 5) return 'behind';
    return 'on-track';
  }, [targetSummary, calculations]);

  const lastTrendData = trendChartData && trendChartData.length > 0
    ? trendChartData[trendChartData.length - 1]
    : null;

  const formattedProjectedDate = useMemo(() => {
    if (targetSummary?.projected_completion_date) { 
      try {
        const date = parseISO(targetSummary.projected_completion_date);
        return format(date, 'MMM d, yyyy');
      } catch (e) {
        console.error("Error parsing projected completion date:", e);
        return "N/A";
      }
    } else if (targetSummary && targetSummary.projected_completion_date === null) {
      return "N/A"; 
    }
    return "Calculating..."; 
  }, [targetSummary]);

  // Determine if projected completion date is past the end date
  const projectionStatus = useMemo(() => {
    if (profile && targetSummary?.projected_completion_date) {
      try {
        const projectedDate = parseISO(targetSummary.projected_completion_date);
        const endDate = parseISO(profile.tracking_end_date);
        
        // If projected date is after end date, show "behind"
        if (projectedDate > endDate) {
          return {
            status: 'behind' as const,
            tooltip: 'Projected to complete after tracking end date'
          };
        }
      } catch (e) {
        console.error("Error comparing projected completion date:", e);
      }
    }
    return {
      status: 'on-track' as const,
      tooltip: 'Projected to complete before or on tracking end date'
    };
  }, [targetSummary, profile]);

  const handleSaveToday = async () => {
    try {
      setSavingToday(true);
      const todayISOString = getTodayISOString();
      await logDailyHours(todayISOString, parseFloat(todayHours));
      setSavingToday(false);
    } catch (error) {
      console.error('Error saving hours:', error);
      setSavingToday(false);
    }
  };

  const handleEditEntry = async (id: string, hours: number, entryDate: string) => {
    try {
      if (isDateInFuture(entryDate)) {
        return;
      }
      
      await editDailyHours(id, hours);
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };
  
  const handleCreateEntry = async (date: string, hours: number) => {
    try {
      if (isDateInFuture(date)) {
        return;
      }
      
      await logDailyHours(date, hours);
    } catch (error) {
      console.error('Error creating entry:', error);
    }
  };

  const handleDeleteEntry = async (id: string, entryDate: string) => {
    try {
      if (isDateInFuture(entryDate)) {
        return;
      }
      
      await deleteDailyHours(id);
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };
  
  const handleSaveHours = async (day: DayEntry, hours: number) => {
    if (day.entry) {
      await handleEditEntry(day.entry.id, hours, day.date);
    } else {
      await handleCreateEntry(day.date, hours);
    }
  };

  const groupDaysByWeek = (days: DayEntry[]) => {
    const weeks: DayEntry[][] = [];
    let currentWeek: DayEntry[] = [];
    
    days.forEach((day, index) => {
      if (day.dayOfWeek === 1 || index === 0) {
        if (currentWeek.length > 0) {
          weeks.push([...currentWeek]);
          currentWeek = [];
        }
      }
      currentWeek.push(day);
    });
    
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    return weeks;
  };

  const remainingHours = targetSummary
    ? targetSummary.remaining_hours
    : calculations?.remainingHoursNeeded ?? 0;

  const totalBilledHours = targetSummary
    ? targetSummary.total_billed_hours
    : calculations?.totalHoursBilled ?? 0;

  const progressPercentage = targetSummary && targetSummary.yearly_target_hours > 0
    ? (targetSummary.total_billed_hours / targetSummary.yearly_target_hours) * 100
    : calculations?.progressPercentage ?? 0;

  const dailyTargetValue = targetSummary && targetSummary.remaining_work_days > 0
    ? Number(targetSummary.remaining_hours) / Number(targetSummary.remaining_work_days)
    : Number(calculations?.requiredDailyHoursForToday ?? 0);

  // Calculate dailyTarget but don't cause error if unused
  const calculatedDailyTarget = !isNaN(dailyTargetValue) ? roundToDecimal(dailyTargetValue) : 0;

  // Using the formattedProjectedDate that was already declared above
  return (
    <AppLayout>
      <ViewTransitionStyles />
      {authLoading || (user && isDashboardLoading) ? (
        <Flex justifyContent="center" alignItems="center" height="60vh" flexDirection="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text>Loading your dashboard...</Text>
        </Flex>
      ) : error ? (
        <Alert status="error" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      ) : !authLoading && user && !profile && !profileLoading ? (
        <Alert status="warning">
          <AlertIcon />
          Could not load profile data. Please try refreshing or ensure setup is complete.
          <Button variant="link" colorScheme="blue" ml={2} onClick={() => navigate('/setup')}>Go to Setup</Button>
          <Button variant="link" colorScheme="blue" ml={2} onClick={() => window.location.reload()}>Refresh</Button>
        </Alert>
      ) : !profile ? (
        <Box>Error: Profile data unavailable.</Box>
      ) : (
        <>
          <Flex justify="space-between" align="center" mb={6}>
            <Box>
              <Heading as="h1" size="lg" mb={1}>
                Dashboard
              </Heading>
              <Text color="gray.600">
                Billable hours progress tracker
              </Text>
            </Box>
            <Button
              as={RouterLink}
              to="/progress-overview"
              rightIcon={<ArrowForwardIcon />}
              colorScheme="teal"
              variant="outline"
              size="sm"
            >
              View Progress Overview
            </Button>
          </Flex>

          {error && (
            <Alert status="error" mb={6}>
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Grid
            templateColumns={{ base: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }}
            templateRows={{ base: 'auto', md: 'auto' }}
            gap={4}
            mb={6}
          >
            <GridItem colSpan={{ base: 1, sm: 2, md: 1 }} rowSpan={1}>
              <Card 
                height="100%" 
                boxShadow="md"
                transition="transform 0.2s"
                _hover={{ transform: 'translateY(-5px)' }}
              >
                <CardHeader pb={2} bg="blue.50" borderBottomWidth="1px">
                  <Heading size="md" color="blue.700">Today's Hours</Heading>
                </CardHeader>
                <CardBody pt={2}>
                  <FormControl>
                    <Flex alignItems="center">
                      <NumberInput
                        value={todayHours}
                        onChange={(valueString) => {
                          const value = parseFloat(valueString);
                          if (value > 24) {
                            setTodayHours("24");
                          } else {
                            setTodayHours(valueString);
                          }
                        }}
                        min={0}
                        max={24}
                        step={0.25}
                        mr={3}
                        w="100px"
                        bg={todayInputBgColor}
                        borderColor="gray.300"
                        borderWidth="1px"
                        _hover={{ bg: todayInputHoverBgColor }}
                        keepWithinRange={true}
                        clampValueOnBlur={true}
                      >
                        <NumberInputField 
                          bg={todayInputBgColor}
                          _hover={{ bg: todayInputHoverBgColor }}
                          _focus={{ bg: 'white', borderColor: 'blue.500' }}
                          textAlign="center"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveToday();
                            }
                          }}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme="blue"
                        onClick={handleSaveToday}
                        isLoading={savingToday}
                        flexGrow={1}
                      >
                        {todayEntry ? 'Update Hours' : 'Log Hours'}
                      </Button>
                    </Flex>
                  </FormControl>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem colSpan={1} rowSpan={1}>
              <MiniStatCard
                label="Daily Target"
                value={`${calculatedDailyTarget} hrs`} // Use calculatedDailyTarget
                helpText="Per work day"
                icon={
                  <ChakraTooltip label="Required daily average to reach your target">
                    <InfoOutlineIcon color="gray.400" />
                  </ChakraTooltip>
                }
              />
            </GridItem>

            <GridItem colSpan={1} rowSpan={1}>
              <MiniStatCard 
                label="Progress"
                value={`${roundToDecimal(progressPercentage)}%`}
                helpText={`${roundToDecimal(totalBilledHours)} hours logged`}
                status={progressStatus}
                statusTooltip="Based on overall progress from start date to now"
                icon={
                  <ChakraTooltip label="Percentage of your annual target hours goal completed">
                    <InfoOutlineIcon color="gray.400" />
                  </ChakraTooltip>
                }
              />
            </GridItem>

            <GridItem colSpan={1} rowSpan={1}>
              <MiniStatCard 
                label="Remaining"
                value={`${roundToDecimal(remainingHours)} hrs`}
                helpText={`${targetSummary ? targetSummary.remaining_work_days : calculations?.remainingWorkDays ?? 0} work days left`}
                status={remainingHours > 0 ? undefined : 'ahead'}
                statusTooltip={remainingHours <= 0 ? "Target hours exceeded" : undefined}
                icon={
                  <ChakraTooltip label="Hours needed to reach your target">
                    <InfoOutlineIcon color="gray.400" />
                  </ChakraTooltip>
                }
              />
            </GridItem>

            <GridItem colSpan={1} rowSpan={1}>
              <MiniStatCard 
                label="Projected Completion"
                value={formattedProjectedDate}
                helpText="Est. date to reach target"
                status={projectionStatus.status}
                statusTooltip={projectionStatus.tooltip}
                icon={
                  <ChakraTooltip label={`Estimated date you will reach your yearly target based on current progress (tracking period ends on ${profile ? format(parseISO(profile.tracking_end_date), 'MMM d, yyyy') : 'N/A'})`}>
                    <InfoOutlineIcon color="gray.400" />
                  </ChakraTooltip>
                }
              />
            </GridItem>

            <GridItem colSpan={{ base: 1, sm: 2, md: 5 }} rowSpan={1}>
              <Card height="100%">
                <CardHeader pb={0}>
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Heading size="sm">Daily Hours</Heading>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Last 14 days of your tracking period
                      </Text>
                    </Box>
                    <ChakraTooltip label="View cumulative hours chart" placement="top" hasArrow>
                      <IconButton 
                        aria-label="View cumulative hours chart"
                        icon={<BarChartIcon />} // Use the defined BarChartIcon
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => setShowCumulativeChart(true)}
                      />
                    </ChakraTooltip>
                  </Flex>
                </CardHeader>
                <CardBody pt={2} pb={3}>
                  <Box className="chart-container" height="140px">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={dailyHoursData}
                        margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis 
                          dataKey="shortDate" 
                          tick={{ fontSize: 10 }} 
                          interval="preserveStartEnd"
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }} 
                          width={25}
                          domain={[0, 'auto']} 
                        />
                        <Tooltip 
                          formatter={(value, name) => {
                            const val = roundToDecimal(value as number);
                            let label;
                            switch(name) {
                              case 'logged': label = 'Logged Hours'; break;
                              case 'deficit': label = 'Hours Below Target'; break;
                              case 'surplus': label = 'Hours Above Target'; break;
                              case 'trend': label = 'Trend'; break;
                              default: label = name;
                            }
                            return [val + ' hrs', label];
                          }}
                          labelFormatter={(label, payload) => {
                            const dataItem = payload && payload.length > 0 ? payload[0].payload : null;
                            if (dataItem) {
                              return `${dataItem.displayDate} (Day ${dataItem.dayIndex} of 14)`;
                            }
                            return label;
                          }}
                        />
                        <Bar 
                          dataKey="logged" 
                          fill={chartConfig.colors.logged}
                          name="logged"
                        />
                        <ReferenceLine 
                          y={targetSummary 
                            ? targetSummary.avg_daily_target 
                            : calculations ? calculations.requiredAverageDailyHours : 0} 
                          stroke={chartConfig.colors.target} 
                          strokeDasharray="3 3" 
                          name="Average Target"
                        />
                        <Line 
                          type="monotone" 
                          dataKey="trend" 
                          stroke={chartConfig.colors.trend} 
                          strokeWidth={2}
                          dot={true}
                          activeDot={{ r: 4 }}
                          name="Trend"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  
                  <Flex justify="center" mt={2}>
                    <HStack spacing={4}>
                      <HStack>
                        <Box w="10px" h="10px" bg={chartConfig.colors.logged} mr={1}></Box>
                        <Text fontSize="xs">Hours</Text>
                      </HStack>
                      <HStack>
                        <Box w="10px" h="10px" borderRadius="full" bg={chartConfig.colors.target} mr={1}></Box>
                        <Text fontSize="xs">Target</Text>
                      </HStack>
                      <HStack>
                        <Box w="10px" h="10px" bg={chartConfig.colors.trend} mr={1}></Box>
                        <Text fontSize="xs">Trend</Text>
                      </HStack>
                    </HStack>
                  </Flex>
                </CardBody>
              </Card>
            </GridItem>

            <GridItem colSpan={{ base: 1, sm: 2, md: 5 }} rowSpan={1}>
              <Card height="100%">
                <CardHeader pb={0}>
                  <Heading size="sm">
                    7-Day Progress Trend
                    <ChakraTooltip 
                      label="Shows whether you're ahead or behind your expected pace, not overall completion. 100% means perfectly on pace (not completion of yearly target)." 
                      placement="top" 
                      hasArrow
                    >
                      <InfoOutlineIcon boxSize="3" ml={2} color="gray.400" verticalAlign="baseline" />
                    </ChakraTooltip>
                  </Heading>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Shows your progress pace relative to expected targets for each day (100% = on pace)
                  </Text>
                </CardHeader>
                <CardBody pt={2} pb={3}>
                  <Box className="chart-container" height="140px">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart
                        data={trendChartData}
                        margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis 
                          dataKey="shortDate" 
                          tick={{ fontSize: 10 }}
                          interval={0}
                        />
                        <YAxis 
                          yAxisId="percent"
                          tick={{ fontSize: 10 }} 
                          width={30}
                          domain={trendChartYDomain}
                          allowDataOverflow={false}
                          label={{ value: '%', position: 'insideLeft', angle: -90, dy: 10, fontSize: 10 }}
                        />
                        <Tooltip 
                          formatter={(value, name) => {
                            const val = typeof value === 'number' ? roundToDecimal(value) : value;
                            let unit = '';
                            let label;
                            
                            switch(name) {
                              case 'percentOfTarget': 
                                label = 'Overall Progress'; 
                                unit = '%';
                                break;
                              case 'cumulativeLogged': 
                                label = 'Total Hours Logged'; 
                                unit = ' hrs';
                                break;
                              case 'cumulativeTarget':
                                label = 'Total Target Hours';
                                unit = ' hrs';
                                break;
                              default: 
                                label = name;
                            }
                            return [`${val}${unit}`, label];
                          }}
                          labelFormatter={(label, payload) => {
                            const dataItem = payload && payload.length > 0 ? payload[0].payload : null;
                            if (dataItem) {
                              const yearlyTarget = targetSummary?.yearly_target_hours || profile?.yearly_target_hours || 0;
                              const percentComplete = yearlyTarget > 0 
                                ? roundToDecimal((dataItem.cumulativeLogged / yearlyTarget) * 100)
                                : 0;
                              
                              return `${dataItem.displayDate} - ${percentComplete}% complete`;
                            }
                            return label;
                          }}
                        />
                        <ReferenceLine 
                          y={100} 
                          yAxisId="percent"
                          stroke={targetColor}
                          strokeDasharray="3 3" 
                        />
                        
                        <Line 
                          type="monotone" 
                          dataKey="percentOfTarget" 
                          stroke={trendColor}
                          strokeWidth={3}
                          yAxisId="percent"
                          dot={{ fill: trendColor, r: 4 }}
                          activeDot={{ r: 6 }}
                          name="percentOfTarget"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  
                  <Flex justify="center" mt={2}>
                    <HStack spacing={2} alignItems="center">
                      <Text fontSize="sm">Current status:</Text>
                      {lastTrendData && <StatusIndicator 
                        status={lastTrendData.status as any} 
                        tooltipText="Based on recent 7-day trend performance"
                      />}
                      {lastTrendData && <Text fontSize="sm" ml={1}>{lastTrendData.percentOfTarget}% of target</Text>}
                    </HStack>
                  </Flex>
                </CardBody>
              </Card>
            </GridItem>
          </Grid>

          <Box mb={8}>
            <Card>
              <CardHeader>
                <Heading size="md">Monthly Breakdown</Heading>
              </CardHeader>
              <CardBody px={2}>
                <Accordion allowMultiple variant="enclosed" defaultIndex={[]}>
                  {sortedMonthlyBreakdown.map((month) => (
                    <AccordionItem key={month.month} border="none" mb={2}>
                      {({ isExpanded }) => (
                        <>
                          <AccordionButton 
                            px={3} 
                            py={2} 
                            _hover={{bg: useColorModeValue('gray.100', 'gray.700')}}
                            borderRadius="md"
                          >
                            <HStack flex="1" spacing={3} textAlign="left">
                              <Icon as={isExpanded ? MinusIcon : AddIcon} boxSize="3" />
                              <Text fontSize="md">{month.displayName}</Text>
                              <Tag colorScheme="blue" size="sm" borderRadius="full" variant="subtle">
                                {roundToDecimal(month.totalHours)} hours
                              </Tag>
                            </HStack>
                          </AccordionButton>
                          <AccordionPanel pt={4} pb={2} px={2}>
                            {month.days && month.days.length > 0 ? (
                              <Stack spacing={1}>
                                {groupDaysByWeek(month.days).map((weekDays) => (
                                  <Box key={weekDays[0].date} mb={3}>
                                    <SimpleGrid columns={1} spacing={1}>
                                      {weekDays.map((day) => (
                                        <DayEntryRow 
                                          key={day.date}
                                          day={day}
                                          onSaveHours={handleSaveHours}
                                          onDeleteEntry={handleDeleteEntry}
                                          workWeekDays={profile?.work_week_days}
                                        />
                                      ))}
                                    </SimpleGrid>
                                  </Box>
                                ))}
                              </Stack>
                            ) : (
                              <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
                                No days to display for this month
                              </Text>
                            )}
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardBody>
            </Card>
          </Box>

          <Modal 
            isOpen={showCumulativeChart} 
            onClose={() => setShowCumulativeChart(false)}
            size="xl"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Heading size="md">Billable Hours Overview</Heading>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Complete tracking period from {profile.tracking_start_date} to present
                </Text>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <Box height="350px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={cumulativeHoursData}
                      margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis 
                        dataKey="shortDate" 
                        tick={{ fontSize: 10 }}
                        interval={Math.floor(cumulativeHoursData.length / 10)}
                      />
                      <YAxis 
                        yAxisId="hours"
                        tick={{ fontSize: 10 }}
                        width={40}
                        domain={[0, 'auto']}
                        label={{ value: 'Hours', position: 'insideLeft', angle: -90, dy: 10, fontSize: 10 }}
                      />
                      <Tooltip 
                        formatter={(value, name) => {
                          const val = typeof value === 'number' ? roundToDecimal(value) : value;
                          let unit = '';
                          let label;
                          
                          switch(name) {
                            case 'cumulativeLogged':
                              label = 'Total Hours Logged';
                              unit = ' hrs';
                              break;
                            case 'logged':
                              label = 'Daily Hours';
                              unit = ' hrs';
                              break;
                            case 'projectedIdealProgress':
                              label = 'Ideal Progress Line';
                              unit = ' hrs';
                              break;
                            default:
                              label = name;
                          }
                          return [`${val}${unit}`, label];
                        }}
                        labelFormatter={(label, payload) => {
                          const dataItem = payload && payload.length > 0 ? payload[0].payload : null;
                          if (dataItem) {
                            const yearlyTarget = targetSummary?.yearly_target_hours || profile?.yearly_target_hours || 0;
                            const percentComplete = yearlyTarget > 0 
                              ? roundToDecimal((dataItem.cumulativeLogged / yearlyTarget) * 100)
                              : 0;
                            
                            return `${dataItem.displayDate} - ${percentComplete}% complete`;
                          }
                          return label;
                        }}
                      />
                      
                      <Bar 
                        dataKey="logged" 
                        fill={chartConfig.colors.logged}
                        name="logged"
                        yAxisId="hours"
                        barSize={4}
                        opacity={0.7}
                      />
                      
                      <Line 
                        type="monotone"
                        dataKey="cumulativeLogged"
                        stroke={chartConfig.colors.completed}
                        strokeWidth={2.5}
                        yAxisId="hours"
                        dot={false}
                        activeDot={{ r: 4 }}
                        name="cumulativeLogged"
                      />
                      
                      <Line
                        type="linear"
                        dataKey="projectedIdealProgress"
                        stroke={chartConfig.colors.target}
                        strokeWidth={1.5}
                        strokeDasharray="5 5"
                        yAxisId="hours"
                        dot={false}
                        name="projectedIdealProgress"
                      />
                      
                      <ReferenceLine
                        y={targetSummary?.yearly_target_hours || profile?.yearly_target_hours || 0}
                        yAxisId="hours"
                        stroke={chartConfig.colors.target}
                        strokeWidth={1}
                        label={{ 
                          value: 'Yearly Target', 
                          position: 'insideBottomRight',
                          fill: chartConfig.colors.target,
                          fontSize: 10
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
                
                <Flex justify="center" mt={2}>
                  <HStack spacing={4} wrap="wrap" justify="center">
                    <HStack>
                      <Box w="10px" h="10px" bg={chartConfig.colors.logged} mr={1}></Box>
                      <Text fontSize="xs">Daily Hours</Text>
                    </HStack>
                    <HStack>
                      <Box w="10px" h="2px" bg={chartConfig.colors.completed} mr={1}></Box>
                      <Text fontSize="xs">Cumulative Hours</Text>
                    </HStack>
                    <HStack>
                      <Box w="10px" h="2px" bg={chartConfig.colors.target} mr={1} borderStyle="dashed"></Box>
                      <Text fontSize="xs">Ideal Progress</Text>
                    </HStack>
                  </HStack>
                </Flex>
                
                <Box mt={2} p={2} bg={useColorModeValue('gray.50', 'gray.700')} borderRadius="md" fontSize="xs">
                  <Flex direction={{ base: 'column', md: 'row' }} justify="space-around" align="center" textAlign="center">
                    <Stat size="sm" px={2} py={1}>
                      <StatNumber fontSize="sm">
                        {roundToDecimal(progressPercentage)}%
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">Overall Completion</StatHelpText>
                    </Stat>
                    <Stat size="sm" px={2} py={1}>
                      <StatNumber fontSize="sm">
                        {roundToDecimal(totalBilledHours)} hrs
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">Total Hours Logged</StatHelpText>
                    </Stat>
                    <Stat size="sm" px={2} py={1}>
                      <StatNumber fontSize="sm">
                        {targetSummary?.yearly_target_hours || profile?.yearly_target_hours || 0} hrs
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">Yearly Target</StatHelpText>
                    </Stat>
                    <Stat size="sm" px={2} py={1}>
                      <StatNumber fontSize="sm" color={remainingHours > 0 ? undefined : 'green.500'}>
                        {roundToDecimal(remainingHours)} hrs
                      </StatNumber>
                      <StatHelpText mb={0} fontSize="xs">Remaining</StatHelpText>
                    </Stat>
                  </Flex>
                </Box>
              </ModalBody>
              <ModalFooter display="none">
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}
    </AppLayout>
  );
};

export default DashboardPage;